/* eslint-disable no-undef */
/* global localStorage console */
/* global document */
import Joyride from "react-joyride";
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "@fluentui/react-components";
import { stopJoyride, nextStep, prevStep, showDownloadStep, resumeFromDownload } from "../state/joyrideSlice";
import WalkthroughStepLookup from "./WalkthroughSteps/WalkthroughLookup";
import { OpenBlue } from "../assets/icons";
import { STARTER_PRESENTATION_PATH, STARTER_PRESENTATION_FILENAME } from "../constants/downloads";
import { useGoogleAnalytics } from "@connectaryal/google-analytics";

const Walkthrough = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { trackEngagement } = useGoogleAnalytics();
  const { run, stepIndex, steps } = useSelector((state) => state.joyride);

  const isInDownloadStep = steps.length > 0 && steps[stepIndex]?.content === "download";
  const joyrideKey = isInDownloadStep ? `joyride-download` : `joyride-normal`;

  const handleJoyrideCallback = (data) => {
    const { action, status, type } = data;

    if (localStorage.getItem("JOYRIDECOMPLETED") === "true") {
      dispatch(stopJoyride());
      return;
    }

    trackEngagement({ type: "tutorial_begin" });
    if (type === "step:after") {
      if (action === "next") {
        dispatch(nextStep());
      } else if (action === "prev") {
        dispatch(prevStep());
      } else if (action === "close") {
        if (isInDownloadStep) {
          dispatch(resumeFromDownload());
        } else {
          trackEngagement({ type: "tutorial_complete" });
          localStorage.setItem("JOYRIDECOMPLETED", "true");
          dispatch(stopJoyride());
        }
      } else {
        console.warn("unrecognized joyride callback action", data);
      }
    } else if (status === "finished" || status === "skipped") {
      if (isInDownloadStep) {
        dispatch(resumeFromDownload());
      } else {
        trackEngagement({ type: "tutorial_complete" });
        localStorage.setItem("JOYRIDECOMPLETED", "true");
        dispatch(stopJoyride());
      }
    } else if (type === "target:not_found") {
      dispatch(nextStep());
    } else {
      console.warn("unrecognized joyride callback type", data);
    }
  };

  const handleTutorialClick = () => {
    const link = document.createElement("a");
    link.href = STARTER_PRESENTATION_PATH;
    link.download = STARTER_PRESENTATION_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    dispatch(showDownloadStep());
  };

  const handleClose = () => {
    dispatch(resumeFromDownload());
  };

  const handleLast = () => {
    trackEngagement({ type: "tutorial_complete" });
    dispatch(stopJoyride());
  };

  const customLocale = {
    skip: (
      <Button onClick={handleTutorialClick} style={{ justifyContent: "left" }}>
        {t("walkthrough.tutorial")} <OpenBlue style={{ width: "16px", height: "16px" }} />
      </Button>
    ),
    last: isInDownloadStep ? (
      <Button onClick={handleClose}>{t("walkthrough.back.to.tips")}</Button>
    ) : (
      <Button onClick={handleLast}>{t("walkthrough.close")}</Button>
    ),
    next: t("walkthrough.next"),
  };

  return (
    <Joyride
      key={joyrideKey}
      steps={steps.map((s) => ({
        ...s,
        content: WalkthroughStepLookup[s.content] ?? s.content,
      }))}
      run={run}
      stepIndex={stepIndex}
      continuous={true}
      showSkipButton={true}
      locale={customLocale}
      disableBeacon
      disableOverlayClose
      disableCloseOnEsc
      disableScrolling
      callback={handleJoyrideCallback}
      styles={{
        options: { zIndex: 2300 },
        buttonNext: { backgroundColor: "#0065FD", fontSize: "14px", fontFamily: "Arial" },
        buttonBack: { color: "#0065FD", fontSize: "14px", fontFamily: "Arial" },
        buttonSkip: { color: "#0065FD", fontSize: "14px", fontFamily: "Arial" },
        tooltip: {
          fontFamily: "Arial",
          maxHeight: "300px",
          position: "relative",
        },
        tooltipContent: {
          fontSize: "14px",
          padding: "8px 0px 0px 0px",
        },
      }}
    />
  );
};

export default Walkthrough;
